import React, { useState, useRef } from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import { FormattedMessage } from '../../../../../util/reactIntl';
import { parseSelectFilterOptions } from '../../../../../util/search';

import { OutsideClickHandler, IconArrowHead } from '../../../../../components';

import css from './FilterCountries.module.css';
import { getCountryFlagById } from '../../../../../util/getCountryFlag';

const ALL_COUNTRIES = 'all-countries';

const normalizeCountrySelection = value => {
  if (Array.isArray(value)) {
    return [...new Set(value.filter(Boolean))];
  }
  if (value == null || value === '') {
    return [];
  }
  if (typeof value === 'string') {
    return [...new Set(parseSelectFilterOptions(value).filter(Boolean))];
  }
  return [];
};

const selectionToQueryParam = codes => (codes.length > 0 ? codes.join(',') : null);

const CountryDropdown = ({
  input,
  className,
  rootClassName,
  countries,
  alignLeft,
  onCountListingsByFilters,
  queryParams,
  setQueryParams,
  dropdownOpenUpwardClass,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [allCountriesExplicit, setAllCountriesExplicit] = useState(false);

  const selectedCodes = normalizeCountrySelection(input.value);

  const syncCountsForSelection = nextCodes => {
    const param = selectionToQueryParam(nextCodes);
    const newQueryParams = { ...queryParams };
    if (param == null) {
      delete newQueryParams.pub_linstingFieldId_country;
    } else {
      newQueryParams.pub_linstingFieldId_country = param;
    }
    setQueryParams(newQueryParams);

    // Exclude selected category so category facet options stay visible
    const { pub_categoryLevel1, ...paramsWithoutCategory } = newQueryParams;
    onCountListingsByFilters({ queryParams: paramsWithoutCategory });
  };

  const toggleDropdown = () => {
    setIsOpen(prev => !prev);
    if (!isOpen) {
      setActiveIndex(-1);
    }
  };

  const handleOptionClick = id => {
    if (id === ALL_COUNTRIES) {
      input.onChange([]);
      syncCountsForSelection([]);
      setAllCountriesExplicit(true);
      setIsOpen(false);
      return;
    }

    setAllCountriesExplicit(false);

    const next = selectedCodes.includes(id)
      ? selectedCodes.filter(c => c !== id)
      : [...selectedCodes, id];

    input.onChange(next);
    syncCountsForSelection(next);
  };

  const handleKeyDown = e => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        setIsOpen(true);
        setActiveIndex(0);
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => {
        if (prev < allOptions.length - 1) {
          return prev + 1;
        }
        return prev;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => {
        if (prev > 0) {
          return prev - 1;
        }
        return prev;
      });
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const selected = allOptions[activeIndex];
      if (selected) {
        handleOptionClick(selected.option);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
    }
  };

  const selectedCountry =
    selectedCodes.length === 1 ? countries.find(country => country.option === selectedCodes[0]) : null;

  const showAllCountriesSummary = allCountriesExplicit && selectedCodes.length === 0;
  const showMultiFlagsOnly = selectedCodes.length > 1;
  const hasSelection = selectedCodes.length > 0 || showAllCountriesSummary;

  const dropdownItemContent = showAllCountriesSummary ? (
    <>
      <span className={classNames(css.flag, css.flagSelected, css.flagAllCountries)}>
        {getCountryFlagById(ALL_COUNTRIES)}
      </span>
      <FormattedMessage id="PageBuilder.SearchCTA.CountryFilter.selectAll" />
    </>
  ) : showMultiFlagsOnly ? (
    <span className={css.selectedFlagRow}>
      {selectedCodes.map(code => (
        <span key={code} className={classNames(css.flag, css.flagSelected)}>
          {getCountryFlagById(code)}
        </span>
      ))}
    </span>
  ) : selectedCountry ? (
    <>
      <span className={classNames(css.flag, css.flagSelected)}>
        {getCountryFlagById(selectedCodes[0])}
      </span>
      {/* {selectedCountry.label} */}
    </>
  ) : (
    <FormattedMessage id="PageBuilder.SearchCTA.CountryFilter.placeholder" />
  );

  const rootClass = rootClassName || css.root;
  const classes = classNames(rootClass, className);

  const allOptions = [
    {
      option: ALL_COUNTRIES,
      label: <FormattedMessage id="PageBuilder.SearchCTA.CountryFilter.selectAll" />,
      count: null,
    },
    ...countries,
  ];

  const optionRefs = useRef([]);

  const isOptionRowSelected = id => {
    if (id === ALL_COUNTRIES) {
      return showAllCountriesSummary;
    }
    return selectedCodes.includes(id);
  };

  return (
    <OutsideClickHandler className={classes} onOutsideClick={() => setIsOpen(false)}>
      <div className={css.dropdownContainer}>
        <div className={css.dropdownItemPlaceholder}>
          <FormattedMessage id="PageBuilder.SearchCTA.CountryFilter.placeholderDescription" />
        </div>
        <div
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-owns="country-listbox"
          aria-controls="country-listbox"
          tabIndex={0}
          onClick={toggleDropdown}
          onKeyDown={handleKeyDown}
          className={classNames(css.toggleButton, {
            [css.unselected]: !hasSelection && !isOpen,
            [css.placeholderOpened]: isOpen && !hasSelection,
          })}
        >
          <span
            className={classNames(css.dropdownItem, hasSelection && css.dropdownItemSelected, {
              [css.dropdownItemWithFlags]: hasSelection,
            })}
          >
            {dropdownItemContent}
          </span>
          <span className={classNames(css.chevron, isOpen && css.isOpen)}>
            <IconArrowHead direction={isOpen ? 'up' : 'down'} />
          </span>
        </div>

        {isOpen && (
          <ul
            className={classNames(css.dropdownContent, dropdownOpenUpwardClass, {
              [css.alignLeft]: alignLeft,
            })}
            role="listbox"
            id="country-listbox"
            aria-multiselectable="true"
          >
            {allOptions.map(({ option: id, label: name, count }, index) => {
              const isRowSelected = isOptionRowSelected(id);
              const isActive = index === activeIndex;
              return (
                <li
                  key={id}
                  ref={option => (optionRefs.current[index] = option)}
                  className={classNames(css.option, {
                    [css.activeOption]: isActive,
                  })}
                  role="option"
                  aria-selected={isRowSelected}
                  onClick={() => handleOptionClick(id)}
                >
                  <span
                    className={classNames(css.multiSelectMark, {
                      [css.multiSelectMarkOn]: isRowSelected,
                    })}
                    aria-hidden
                  />
                  <span
                    className={isRowSelected ? css.dropdownItemBorderSelected : css.dropdownItemBorder}
                  />
                  <span className={classNames(css.flag, id === ALL_COUNTRIES && css.flagAllCountries)}>
                    {getCountryFlagById(id)}
                  </span>
                  {name}
                  <span className={css.count}>
                    {id !== ALL_COUNTRIES
                      ? count
                      : allOptions.reduce((acc, curr) => acc + Number(curr.count), 0)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </OutsideClickHandler>
  );
};

const FilterCountries = props => {
  const { className, rootClassName, ...rest } = props;
  return (
    <Field
      {...rest}
      name="pub_linstingFieldId_country"
      component={CountryDropdown}
      className={className}
      rootClassName={rootClassName}
    />
  );
};

export default FilterCountries;
